import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Chip,
    TextField,
    InputAdornment
} from '@mui/material';
import { CheckCircle, Search, Filter, SortAsc, SortDesc, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const AchatsEnAttenteReception = ({ 
    onUpdate,
    getStatutLabel,
    getProduitLabel,
    getFournisseurLabel,
    formatDate,
    formatCurrency,
    title = "Achats en attente de réception",
    showTitle = true,
    customStyles = {},
    achatsUpdateTrigger
}) => {
    const [loading, setLoading] = useState(false);
    const [achats, setAchats] = useState([]);
    const [statuts, setStatuts] = useState([]);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'dateAchat',
        direction: 'desc'
    });
    const [showSearch, setShowSearch] = useState(false);
    const [dateLivraison, setDateLivraison] = useState('');

    // Charger les statuts
    const fetchStatuts = async () => {
        try {
            const response = await api.get('/api/statut');
            // Correction : toujours stocker un tableau
            let statutsData = response.data;
            if (!Array.isArray(statutsData) && statutsData && Array.isArray(statutsData.data)) {
                statutsData = statutsData.data;
            }
            setStatuts(Array.isArray(statutsData) ? statutsData : []);
        } catch (error) {
            console.error("Erreur lors du chargement des statuts:", error);
            setNotification({
                open: true,
                message: "Erreur lors du chargement des statuts",
                severity: "error"
            });
        }
    };

    // Charger les achats en attente de réception
    const fetchAchatsEnAttente = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/achats/en-attente');
            setAchats(response.data);
        } catch (error) {
            console.error("Erreur lors du chargement des achats en attente:", error);
            setNotification({
                open: true,
                message: "Erreur lors du chargement des achats en attente",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatuts();
        fetchAchatsEnAttente();
    }, [achatsUpdateTrigger]);

    // Fonction pour obtenir les achats filtrés et triés
    const filteredAndSortedAchats = useMemo(() => {
        if (!Array.isArray(achats)) {
            console.warn("achats n'est pas un tableau:", achats);
            return [];
        }

        let filtered = [...achats];
        
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(achat => 
                (getProduitLabel(achat.Produit) || '').toLowerCase().includes(searchLower) ||
                (getFournisseurLabel(achat.Fournisseur) || '').toLowerCase().includes(searchLower) ||
                (achat.SKUID || '').toLowerCase().includes(searchLower)
            );
        }

        if (dateLivraison) {
            filtered = filtered.filter(achat => {
                const achatDate = new Date(achat.dateLivraison);
                const filterDate = new Date(dateLivraison);
                return achatDate.toDateString() === filterDate.toDateString();
            });
        }

        // Tri
        return filtered.sort((a, b) => {
            if (sortConfig.key === 'dateAchat') {
                const dateA = new Date(a.dateAchat || 0);
                const dateB = new Date(b.dateAchat || 0);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            if (sortConfig.key === 'prixAchat') {
                const prixA = Number(a.prixAchat) || 0;
                const prixB = Number(b.prixAchat) || 0;
                return sortConfig.direction === 'asc' ? prixA - prixB : prixB - prixA;
            }
            if (sortConfig.key === 'Quantité') {
                const quantiteA = Number(a.Quantité) || 0;
                const quantiteB = Number(b.Quantité) || 0;
                return sortConfig.direction === 'asc' ? quantiteA - quantiteB : quantiteB - quantiteA;
            }
            return 0;
        });
    }, [achats, searchTerm, dateLivraison, sortConfig, getProduitLabel, getFournisseurLabel]);

    // Fonction pour mettre à jour le statut d'un achat
    const handleUpdateStatut = async (achatId) => {
        try {
            if (loading) return;
            setLoading(true);

            // Vérifier si les statuts sont chargés
            if (!statuts || statuts.length === 0) {
                throw new Error("Les statuts ne sont pas encore chargés");
            }

            const statutExpedition = statuts.find(s => s.Titre === "En attente d'expédition");
            if (!statutExpedition) {
                throw new Error("Statut 'En attente d'expédition' non trouvé");
            }

            await api.put(`/api/achats/${achatId}`, {
                Statut: statutExpedition.id
            });

            if (onUpdate) {
                await onUpdate();
            }

            setNotification({
                open: true,
                message: "Statut mis à jour avec succès",
                severity: "success"
            });
        } catch (error) {
            console.error("Erreur lors de la mise à jour du statut:", error);
            setNotification({
                open: true,
                message: error.message || "Erreur lors de la mise à jour du statut",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour gérer le tri
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Fonction pour obtenir l'icône de tri
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />;
    };

    const columns = [
        {
            field: 'dateLivraison',
            headerName: 'Date de livraison',
            width: 150,
            valueFormatter: (params) => formatDate(params.value),
            renderCell: (params) => {
                if (!params.value) return '';
                // On ignore l'heure pour la comparaison
                const today = new Date();
                today.setHours(0,0,0,0);
                const deliveryDate = new Date(params.value);
                deliveryDate.setHours(0,0,0,0);
                const diffDays = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24));
                let color = 'inherit';
                let icon = null;
                if (diffDays < 0) {
                    color = '#d32f2f'; // Rouge pour les dates dépassées
                    icon = <AlertTriangle size={16} color={color} />;
                } else if (diffDays >= 0 && diffDays <= 3) {
                    color = '#ed6c02'; // Orange pour les dates proches (aujourd'hui à J+3)
                    icon = <AlertTriangle size={16} color={color} />;
                } else if (diffDays > 3 && diffDays <= 7) {
                    color = '#2e7d32'; // Vert pour les dates dans la semaine
                    icon = <CheckCircle size={16} color={color} />;
                } else {
                    color = 'inherit';
                    icon = null;
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon}
                        <span style={{ color }}>{formatDate(params.value)}</span>
                    </Box>
                );
            },
        },
        {
            field: 'Produit',
            headerName: 'Produit',
            width: 200,
            valueFormatter: (params) => getProduitLabel(params.value),
            renderCell: (params) => {
                const produitLabel = getProduitLabel(params.value);
                return produitLabel || params.value;
            },
            maxWidth: 160
        },
        {
            field: 'Fournisseur',
            headerName: 'Fournisseur',
            width: 200,
            valueFormatter: (params) => getFournisseurLabel(params.value),
            renderCell: (params) => {
                const fournisseurLabel = getFournisseurLabel(params.value);
                return fournisseurLabel || params.value;
            },
            maxWidth: 160
        },
        {
            field: 'Quantité',
            headerName: 'Quantité',
            width: 100,
            valueFormatter: (params) => params.value,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title="Marquer comme reçu">
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleUpdateStatut(params.value)}
                            startIcon={<CheckCircle size={16} />}
                            sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: '0.85rem', lineHeight: 1.2 }}
                        >
                            Reçu
                        </Button>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ mt: 4, mb: 4, width: '100%', ...customStyles }}>
            {showTitle && (
                <Typography variant="h5" gutterBottom>
                    {title}
                </Typography>
            )}
            <Paper sx={{ p: 1.5, width: '100%', boxShadow: 1, fontSize: '0.92rem', minWidth: 0, overflowX: 'auto' }}>
                {/* Barre de recherche et filtres */}
                <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton
                        onClick={() => setShowSearch((prev) => !prev)}
                        size="small"
                        color={showSearch ? "primary" : "default"}
                    >
                        <Search size={20} />
                    </IconButton>
                    
                    {showSearch && (
                        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Rechercher un produit, fournisseur ou SKU..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={16} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField
                                size="small"
                                type="date"
                                label="Date de livraison"
                                value={dateLivraison}
                                onChange={(e) => setDateLivraison(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Chip
                                icon={<Filter size={16} />}
                                label={`${filteredAndSortedAchats.length} achat${filteredAndSortedAchats.length > 1 ? 's' : ''}`}
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredAndSortedAchats.length === 0 ? (
                    <Typography variant="body1" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        {searchTerm ? "Aucun résultat trouvé" : "Aucun achat en attente de réception"}
                    </Typography>
                ) : (
                    <TableContainer sx={{ width: '100%', minWidth: 0 }}>
                        <Table size="small" sx={{ width: '100%', minWidth: 0 }}>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.field}
                                            sortDirection={sortConfig.key === column.field ? sortConfig.direction : false}
                                            sx={{
                                                fontSize: '0.92rem',
                                                px: 1,
                                                py: 0.5,
                                                whiteSpace: 'nowrap',
                                                maxWidth: 160,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minWidth: 60,
                                                fontWeight: 500,
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                                                 onClick={() => handleSort(column.field)}>
                                                {column.headerName}
                                                {getSortIcon(column.field)}
                                            </Box>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAndSortedAchats.map((achat) => (
                                    <TableRow 
                                        key={achat.id}
                                        hover
                                        sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                                    >
                                        {columns.map((column) => (
                                            <TableCell key={column.field} sx={{
                                                fontSize: '0.92rem',
                                                px: 1,
                                                py: 0.5,
                                                whiteSpace: 'nowrap',
                                                maxWidth: 160,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minWidth: 60
                                            }}>
                                                {column.renderCell
                                                    ? (column.field === 'actions'
                                                        ? column.renderCell({ value: achat.id })
                                                        : (
                                                            <Tooltip title={(() => {
                                                                if (column.field === 'Produit') return getProduitLabel(achat.Produit);
                                                                if (column.field === 'Fournisseur') return getFournisseurLabel(achat.Fournisseur);
                                                                return achat[column.field];
                                                            })()} placement="top" arrow>
                                                                <span style={{ display: 'inline-block', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>
                                                                    {column.renderCell({ value: achat[column.field] })}
                                                                </span>
                                                            </Tooltip>
                                                        )
                                                    )
                                                    : (column.field === 'dateLivraison'
                                                        ? (() => {
                                                            const date = new Date(achat[column.field]);
                                                            return isNaN(date) ? '' : date.toLocaleDateString('fr-FR');
                                                          })()
                                                        : achat[column.field]
                                                    )
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AchatsEnAttenteReception; 